import { useSuspenseQuery } from '@apollo/client';
import DevicesListItem from '@features/access/components/devicesTab/DevicesListItem';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, TextField, Typography, ButtonGroup, Button, Tooltip } from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect, useState, type FC } from 'react';
import { FixedSizeList } from 'react-window-1.8.10';
import GET_DEVICES_ACCESS_FOR_USER from '../graphql/queries/getDevicesAccessForUser';
import { DeviceUserValue } from '../types';

interface DevicesAccessForUserProps {
  userId: number;
  value: DeviceUserValue[];
  onChange: (values: DeviceUserValue[]) => void;
  /** @default 450 */
  height?: number;
}

const DevicesAccessForUserInput: FC<DevicesAccessForUserProps> = ({
  userId,
  value,
  onChange,
  height,
}) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(GET_DEVICES_ACCESS_FOR_USER, {
    variables: {
      userId,
      devicesInput: {
        ignoreAccess: true,
      },
    },
  });
  const [search, setSearch] = useState('');
  const [filteredRelations, setFilteredRelations] = useState(data.user.devices);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);

  useEffect(() => {
    // Orden base alfabético por nombre (fallback deviceID). visibleToUser NO ordena — señal visual.
    let filteredItems = [...data.user.devices].sort((a, b) => {
      const nameA = a.device.profile.name || a.device.id;
      const nameB = b.device.profile.name || b.device.id;
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true });
    });

    if (selectedOrganizationId != null) {
      filteredItems = filteredItems.filter(
        (item) => item.device.profile.organization?.id === selectedOrganizationId,
      );
    }

    if (search) {
      const fuse = new Fuse(filteredItems, {
        keys: ['device.id', 'device.profile.name'],
      });
      filteredItems = fuse.search(search).map(({ item }) => item);
    }

    setFilteredRelations(filteredItems);
  }, [search, data.user.devices, selectedOrganizationId]);

  const onSearchChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value);
  };

  const withAccessNumber = data.user.devices.filter(({ hasAccess }) => hasAccess).length;
  const totalNumber = data.user.devices.length;

  const deviceOrganizationIds = Array.from(
    new Set(
      data.user.devices
        .map((device) => device.device.profile.organization?.id)
        .filter((organizationId) => organizationId),
    ),
  ) as number[];

  const handleBulkAccess = (grantAccess: boolean) => {
    const newModifications = filteredRelations
      .filter((relation) => relation.hasAccess !== grantAccess)
      .map((relation) => ({
        id: relation.id,
        hasAccess: grantAccess,
      }));

    const unrelatedModifications = value.filter(
      (item) => !filteredRelations.some((relation) => relation.id === item.id),
    );

    onChange([...unrelatedModifications, ...newModifications]);
  };

  return (
    <>
      <Box mb={1} display="flex" justifyContent="stretch" alignItems="center" gap={2}>
        <TextField
          value={search}
          onChange={onSearchChange}
          size="small"
          label={_(msg`Buscar`)}
          sx={{ flex: 3 }}
        />

        <StandardAutocompleteIdsWithFallback
          sx={{ flex: 2 }}
          size="small"
          queryOptions={{
            organizations: {
              ids: deviceOrganizationIds,
            },
          }}
          dataType="organizations"
          value={selectedOrganizationId}
          disableClearable={false}
          onChange={(_, value) => {
            setSelectedOrganizationId(value);
          }}
        />

        <Typography textAlign="right">
          <Trans>
            Con acceso a <b>{withAccessNumber}</b> dispositivos de <b>{totalNumber}</b>
          </Trans>
        </Typography>

        <ButtonGroup>
          <Tooltip title={_(msg`Seleccionar todos`)}>
            <Button onClick={() => handleBulkAccess(true)}>{_(msg`Todos`)}</Button>
          </Tooltip>
          <Tooltip title={_(msg`Seleccionar ninguno`)}>
            <Button onClick={() => handleBulkAccess(false)}>{_(msg`Ninguno`)}</Button>
          </Tooltip>
        </ButtonGroup>
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1}>
        <FixedSizeList
          height={height ?? 450}
          width="100%"
          itemSize={56}
          itemCount={filteredRelations.length}
          itemKey={(index: number) => filteredRelations[index].id}
        >
          {({ style, index }: { style: React.CSSProperties; index: number }) => {
            const deviceUserRelation = filteredRelations[index];
            const { device } = deviceUserRelation;
            const { hasAccess: formHasAccess } =
              value.find(({ id: relationId }) => relationId === deviceUserRelation.id) ?? {};
            const hasAccess = formHasAccess ?? deviceUserRelation.hasAccess;
            const modified = formHasAccess != null;
            const lastItem = index === filteredRelations.length - 1;
            const onClick = () => {
              const newValues = value.filter(
                ({ id: relationId }) => relationId !== deviceUserRelation.id,
              );
              const newValue = {
                id: deviceUserRelation.id,
                hasAccess: !hasAccess,
              };
              if (newValue.hasAccess === deviceUserRelation.hasAccess) onChange(newValues);
              else onChange([...newValues, newValue]);
            };

            return (
              <DevicesListItem
                style={style}
                key={index}
                deviceId={device.id}
                deviceName={device.profile.name}
                deviceOrganization={device.profile.organization}
                deviceIrrigationName={device.profile.irrigation?.name}
                onClick={onClick}
                hasAccess={hasAccess}
                modified={modified}
                lastItem={lastItem}
                visibleToUser={deviceUserRelation.visibleToUser}
                operationalState={device.profile.operationalState}
              />
            );
          }}
        </FixedSizeList>
      </Box>
    </>
  );
};

export default DevicesAccessForUserInput;
